import { useCallback, useMemo, useState, type ReactElement } from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatUtcDate } from '@/lib/date-utils';
import {
  DEFAULT_EXPIRATION_PRESET,
  endOfPickedDayUtc,
  startOfTomorrowLocal,
  EXPIRATION_PRESET_LABELS,
  EXPIRATION_PRESET_ORDER,
  MANAGEMENT_KEY_NAME_MAX_LENGTH,
  resolveExpiration,
  type ExpirationPreset,
} from '@/lib/management-key-utils';

interface CreateManagementApiKeyDialogProps {
  /**
   * Dismiss the dialog. The parent unmounts on this — see the component doc.
   */
  onClose: () => void;
  /**
   * Submits the key. Rejecting is expected — the caller surfaces the error and
   * this dialog stays open so the user does not lose what they typed.
   */
  onCreate: (input: { name: string; expiresAt?: Date }) => Promise<void>;
  isPending: boolean;
}

/**
 * Create-key form for management API keys (KIR-3748).
 *
 * **Mounted only while open.** The parent renders it conditionally rather than
 * passing an `isOpen` prop, so every dismissal path — Cancel, Esc, backdrop,
 * and the programmatic close after a successful create — unmounts the form and
 * resets it. Radix fires `onOpenChange` only for user-driven dismissals, so a
 * handler-based reset would leave the previous key's name in the field the next
 * time the dialog opened.
 *
 * Client-side validation here is load-bearing, not cosmetic: the contract's
 * `expiresAt` carries a `.refine(future)` that throws a `SchemaParseError`
 * *before the request is sent*, so an empty name or a past date would surface
 * as an opaque client crash rather than a field error. The calendar therefore
 * disables everything before tomorrow, and Create stays disabled until the name
 * is non-empty and a custom date (if chosen) has been picked.
 */
export function CreateManagementApiKeyDialog({
  onClose,
  onCreate,
  isPending,
}: CreateManagementApiKeyDialogProps): ReactElement {
  const [name, setName] = useState('');
  const [preset, setPreset] = useState<ExpirationPreset>(
    DEFAULT_EXPIRATION_PRESET
  );
  const [customDate, setCustomDate] = useState<Date | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Memoized so react-day-picker, which compares the matcher by reference,
  // does not re-evaluate every disabled day on each render.
  const minDate = useMemo(() => startOfTomorrowLocal(), []);

  const trimmedName = name.trim();
  const canSubmit =
    trimmedName.length > 0 &&
    trimmedName.length <= MANAGEMENT_KEY_NAME_MAX_LENGTH &&
    (preset !== 'custom' || customDate !== undefined) &&
    !isPending;

  // Blocks Esc and backdrop clicks while the create is in flight, matching the
  // disabled Cancel button — dismissing mid-request would tear down the only
  // component that can catch and surface the result.
  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open && !isPending) onClose();
    },
    [isPending, onClose]
  );

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) return;
    const expiresAt =
      preset === 'custom'
        ? // End of the chosen calendar day in UTC. Built from the day's *local*
          // parts — see `endOfPickedDayUtc` for why the UTC parts are wrong.
          customDate && endOfPickedDayUtc(customDate)
        : resolveExpiration(preset);

    await onCreate({ name: trimmedName, expiresAt });
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create management API key</DialogTitle>
          <DialogDescription>
            The key is shown once, immediately after it is created. Store it
            somewhere safe — it cannot be retrieved again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="management-key-name">Name</Label>
            <Input
              id="management-key-name"
              value={name}
              maxLength={MANAGEMENT_KEY_NAME_MAX_LENGTH}
              placeholder="e.g. Splunk connector"
              onChange={event => setName(event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="management-key-expiration">Expiration</Label>
            <Select
              value={preset}
              onValueChange={value => {
                setPreset(value as ExpirationPreset);
                if (value !== 'custom') setCustomDate(undefined);
              }}
            >
              <SelectTrigger id="management-key-expiration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXPIRATION_PRESET_ORDER.map(value => (
                  <SelectItem key={value} value={value}>
                    {EXPIRATION_PRESET_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {preset === 'custom' && (
            <div className="space-y-1.5">
              <Label>Expires on</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start font-normal"
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {/* Formatted from the normalised instant, not the raw
                        picked day: `formatUtcDate` reads UTC parts, so labelling
                        `customDate` directly showed the previous day east of
                        UTC — the same off-by-one as the submitted value. */}
                    {customDate
                      ? formatUtcDate(endOfPickedDayUtc(customDate))
                      : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={customDate}
                    onSelect={date => {
                      setCustomDate(date);
                      if (date) setCalendarOpen(false);
                    }}
                    disabled={{ before: minDate }}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {isPending ? 'Creating…' : 'Create key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
